import {
  Box,
  Button,
  Container,
  Flex,
  Loader,
  Stack,
  Text,
} from '@mantine/core';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import posthog from 'posthog-js';
import { POSTHOG_KEY } from 'consts';
import { GreyBox, SignatureQueue, DocumentBox } from './components';
import { useExtDocumentSignaturesController } from './hooks';
import { NotFound } from '../NotFound';
import { Footer } from './components/Footer';
import { CompanyBox } from './components/CompanyBox';
import { useGetV1Url } from '../../hooks';
import { LanguageSelect } from '../../components/NonLoggedLayout/LanguageSelect';

type FormsRoutingProps = {
  docSlug: string;
};

export const ExtDocumentSignatures = () => {
  const { docSlug } = useParams() as FormsRoutingProps;
  const { data, signatures, isLoading } =
    useExtDocumentSignaturesController(docSlug);
  const { t } = useTranslation('documentSignatures');
  const url = useGetV1Url();

  /**
   * Always render component because we need to save the same hook calls between renders
   */
  const page404 = NotFound();

  /**
   * Loading state
   */
  if (isLoading) {
    return (
      <Flex
        justify="center"
        align="center"
        sx={{ height: '100%', width: '100%' }}
      >
        <Loader />
      </Flex>
    );
  }

  /**
   * Display 404 page if data is not found
   */
  if (!data) {
    return page404;
  }

  /**
   * Get title based on the stage
   */
  const getTitle = () =>
    data.data.finished ? t('stage.finished') : t('stage.notFinished');

  /**
   * Display page with data
   */
  return (
    <Flex direction="column" justify="space-between" sx={{ height: '100%' }}>
      <Container p={32} size="xs" w="100%">
        <Flex py={32} justify="flex-end">
          <LanguageSelect />
        </Flex>
        <Stack spacing="xl">
          <Text fw={600} fz="30px">
            {getTitle()}
          </Text>
          <Stack spacing={16}>
            <DocumentBox name={data.data.name} />
            {data.data.company && data.data.company !== '' && (
              <CompanyBox name={data.data.company} />
            )}
            <GreyBox>
              <Stack w="100%">
                <Text fw={500} fz="lg" color="gray.6">
                  {t('details')}
                </Text>
                <SignatureQueue queue={signatures} />
                <Flex justify="center">
                  <Button
                    variant="default"
                    onClick={() => {
                      posthog.capture(
                        POSTHOG_KEY.DOCUMENT_SIGNATURES_PAGE_GOTO_APP,
                      );
                      window.open(
                        url(`contract`, {
                          documentId: data.data.id,
                        }),
                        '_blank',
                      );
                    }}
                  >
                    {t('goToDocument')}
                  </Button>
                </Flex>
              </Stack>
            </GreyBox>
          </Stack>

          <Box>
            <Text fz="md">
              <Trans
                i18nKey="documentSignatures:govPage"
                components={{
                  a: (
                    <Text
                      fz="md"
                      component="a"
                      target="_blank"
                      onClick={() => {
                        posthog.capture(
                          POSTHOG_KEY.DOCUMENT_SIGNATURES_PAGE_GOTO_GOVERNMENT,
                        );
                      }}
                      href={data.data.verificationUrl}
                      variant="link"
                    />
                  ),
                }}
              />
            </Text>
          </Box>
        </Stack>
      </Container>
      <Footer />
    </Flex>
  );
};
